import { useState, useEffect } from "react";
import {
  Card,
  Flex,
  Typography,
  InputNumber,
  Spin,
  Collapse,
  Button,
  Select,
} from "antd";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

import { POLYGONS } from "../ThreeJs/Global/variables";
import { useParams } from "react-router-dom";
import { fetchProduction } from "../helpers/API";
import { generatePvwattsReport } from "../helpers/GeneratePVWattsReport";

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

const { Text } = Typography;
const { Panel } = Collapse;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ElectricityGeneration = ({ shadingValues, panelList, currentDesign }) => {
  
  const [segmentProductions, setSegmentProductions] = useState([]);
  const [segmentRequestParams, setSegmentRequestParams] = useState([]);
  const [totalLoss, setTotalLoss] = useState(0);
  const [loading, setLoading] = useState(false);
  const { token } = useParams();

  const latitude = parseFloat(currentDesign.latitude);
  const longitude = parseFloat(currentDesign.longitude);

  const [systemDetails, setSystemDetails] = useState({
    module_type: 0,     // 0=Standard, 1=Premium, 2=Thin film
    dc_ac_ratio: 1.5,   // default PVWatts
    inv_eff: 97.0,      // %
    dataset: "nsrdb",   // nsrdb | tmy2 | tmy3 | intl
  });

  const handleSystemDetailChange = (field, value) => {
    setSystemDetails((prev) => ({ ...prev, [field]: value }));
  };

  const [values, setValues] = useState({
    moduleNameplateRating: 1,
    mismatch: 1.5,
    connections: 0.5,
    lightInducedDegradation: 1.5,
    wiring: 2,
    soiling: 2,
    soilingPeriod: "Annual",
    availability: 1,
    age: 0,
    snow: 4,
    snowPeriod: "Annual",
    other: 0,
  });

  const calculateTotalLoss = () => {
    const total =
      values.moduleNameplateRating +
      values.mismatch +
      values.connections +
      values.lightInducedDegradation +
      values.wiring +
      values.soiling +
      values.availability +
      values.age +
      values.snow +
      values.other;

    return total > 100 ? 100 : parseFloat(total.toFixed(2));
  };

  useEffect(() => {
    setTotalLoss(calculateTotalLoss());
  }, [values]);

  const buildSegmentParams = () => {
    const dynamicLoss = calculateTotalLoss();

    let panelPmax = 0;
    const samplePanel = POLYGONS.flatMap((seg) => seg.panels || [])[0];
    if (samplePanel) {
      const matchedPanel = panelList.find((p) => p.id === samplePanel.id);
      if (matchedPanel) panelPmax = parseInt(matchedPanel.pmax, 10) / 1000;
    }

    return POLYGONS.filter((seg) => seg.panels?.length > 0).map((segment) => ({
      lat: latitude,
      lon: longitude,
      tilt: segment.details?.tilt || 33,
      azimuth: segment.details?.azimuth || 180,
      capacity: (segment.panels?.length || 0) * panelPmax,
      module_type: systemDetails.module_type,
      array_type: 0,
      losses:
        dynamicLoss +
        (100 -
          (segment.irradiance ? segment.irradiance.ASA * 100 : 95).toFixed(2)),
      soiling:
        values.soilingPeriod === "Monthly"
          ? "2|2|3|4|5|5|6|5|4|3|3|2"
          : 3,
      albedo: 0.2,
      bifaciality: 0,
      gcr: 0.4,
      dc_ac_ratio: systemDetails.dc_ac_ratio,
      inv_eff: systemDetails.inv_eff,
      dataset: systemDetails.dataset,
    }));
  };

  const simulateSystem = async () => {
    setLoading(true);
    try {
      const segmentParams = buildSegmentParams();

      console.log("🔹 PVWatts INPUT PARAMS (per segment):", segmentParams);

      const res = await fetchProduction(segmentParams);

      console.log("🔹 PVWatts RAW RESPONSE:", res?.data);

      setSegmentProductions(res.data || []);
      setSegmentRequestParams(segmentParams || []);
    } catch (err) {
      console.error(" Error in simulateSystem:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token && POLYGONS.length > 0) {
      simulateSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleValueChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value ?? 0 }));
  };

  const EditableField = ({ label, field, value }) => (
    <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
      <Text style={{ fontSize: 12 }}>{label}</Text>
      <Flex align="center" gap={8}>
        <InputNumber
          size="small"
          value={value}
          onChange={(val) => handleValueChange(field, val)}
          min={0}
          max={100}
          step={0.1}
          style={{ width: 55, fontSize: 12 }}
          controls={false}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          %
        </Text>
      </Flex>
    </Flex>
  );

  // ---- TOTAL SYSTEM AGGREGATES ----
  const totalProduction = segmentProductions.reduce(
    (sum, seg) => sum + (seg?.production?.outputs?.ac_annual ?? 0),
    0
  );

  const monthlyProduction =
    segmentProductions.length > 0
      ? MONTHS.map((_, i) =>
          segmentProductions.reduce(
            (sum, seg) =>
              sum + (seg?.production?.outputs?.ac_monthly?.[i] || 0),
            0
          )
        )
      : [];

  const totalPanels = POLYGONS.reduce(
    (sum, seg) => sum + (seg.panels?.length || 0),
    0
  );

  const samplePanel = POLYGONS.flatMap((seg) => seg.panels || [])[0];
  let panelPmaxKw = 0;
  if (samplePanel) {
    const matched = panelList.find((p) => p.id === samplePanel.id);
    if (matched) panelPmaxKw = parseInt(matched.pmax, 10) / 1000;
  }

  const dcSystemSizeKw = totalPanels * panelPmaxKw;

  const chartData = {
    labels: MONTHS,
    datasets: [
      {
        label: "Monthly Production (kWh)",
        backgroundColor: "#1677ff",
        data: monthlyProduction,
      },
    ],
  };

  const firstSegment = POLYGONS.find((s) => s.panels?.length > 0);
  const arrayTilt = firstSegment?.details?.tilt ?? 20;
  const arrayAzimuth = firstSegment?.details?.azimuth ?? 180;

  const solarRadiationMonthly =
    segmentProductions.length > 0
      ? MONTHS.map((_, idx) => {
          let total = 0,
            count = 0;
          segmentProductions.forEach((seg) => {
            const arr = seg?.production?.outputs?.solrad_monthly || [];
            if (arr[idx] != null) {
              total += arr[idx];
              count++;
            }
          });
          return count ? total / count : null;
        })
      : [];

  // ✅ DC Capacity Factor for the whole system
  const dcCapacityFactor =
    dcSystemSizeKw > 0
      ? (totalProduction / (dcSystemSizeKw * 8760)) * 100
      : null;

  // ✅ Segments with panels (same order as buildSegmentParams / PVWatts segments)
  const segmentsWithPanels = POLYGONS.filter((seg) => seg.panels?.length > 0);

  // ✅ Per-segment monthly irradiance loss (%), using polygon.irradiance.monthlyASA
  const segmentMonthlyIrradianceLoss =
    segmentsWithPanels.length > 0
      ? segmentsWithPanels.map((seg) => {
          const monthlyASA = seg.irradiance?.monthlyASA;
          if (!monthlyASA || !Array.isArray(monthlyASA)) return [];
          return MONTHS.map((_, monthIdx) => {
            const m = monthlyASA[monthIdx];
            const asaVal = m?.asa;
            return typeof asaVal === "number" && !Number.isNaN(asaVal)
              ? (1 - asaVal) * 100 // percentage loss
              : null;
          });
        })
      : [];

  // ✅ System-level monthly irradiance loss: capacity-weighted average of segment losses
  const monthlyIrradianceLoss =
    segmentsWithPanels.length > 0
      ? MONTHS.map((_, monthIdx) => {
          let weightedSum = 0;
          let capacitySum = 0;

          segmentsWithPanels.forEach((seg) => {
            const monthlyASA = seg.irradiance?.monthlyASA;
            if (!monthlyASA || !monthlyASA[monthIdx]) return;

            const asaVal = monthlyASA[monthIdx].asa;
            const panelCount = seg.panels?.length || 0;
            if (
              typeof asaVal === "number" &&
              !Number.isNaN(asaVal) &&
              panelCount > 0 &&
              panelPmaxKw > 0
            ) {
              const capKw = panelCount * panelPmaxKw;
              const lossPct = (1 - asaVal) * 100;
              weightedSum += lossPct * capKw;
              capacitySum += capKw;
            }
          });

          return capacitySum > 0 ? weightedSum / capacitySum : null;
        })
      : [];

  console.log("🔹 System Monthly Irradiance Loss (%):", monthlyIrradianceLoss);
  console.log(
    "🔹 Segment Monthly Irradiance Loss (% per segment):",
    segmentMonthlyIrradianceLoss
  );

  // ✅ Derive system-level PV params from first request
  let derivedModuleType = null;
  let derivedArrayType = null;
  let derivedDcToAcRatio = null;
  let derivedInverterEff = null;
  let derivedGcr = null;
  let derivedAlbedo = null;
  let derivedBifacial = null;

  if (segmentRequestParams.length > 0) {
    const req0 = segmentRequestParams[0];

    const toNum = (v) =>
      typeof v === "number"
        ? v
        : typeof v === "string"
        ? Number(v)
        : null;

    derivedModuleType = toNum(req0.module_type);
    derivedArrayType = toNum(req0.array_type);
    derivedDcToAcRatio = toNum(req0.dc_ac_ratio);
    derivedInverterEff = toNum(req0.inv_eff);
    derivedGcr = toNum(req0.gcr);
    derivedAlbedo = toNum(req0.albedo);
    derivedBifacial = toNum(req0.bifaciality ?? req0.bifacial);
  }

  return (
    <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 16 }}>
      <Flex vertical style={{ height: "100%" }}>
        <Flex style={{ gap: "10px" }}>
          {/* Simulate Button */}
          <Button
            type="default"
            style={{ width: "100%", marginBottom: 10 }}
            onClick={simulateSystem}
          >
            Simulate System
          </Button>

          {/* PDF Button */}
          <Button
            type="primary"
            style={{ width: "100%", marginBottom: 10 }}
            onClick={() =>
              generatePvwattsReport({
                MONTHS,
                monthlyProduction,
                solarRadiationMonthly,
                totalProduction,
                totalLoss,
                currentDesign,
                latitude,
                longitude,
                dcSystemSizeKw,
                arrayTilt,
                arrayAzimuth,

                // system-level PV params
                moduleType: derivedModuleType,
                arrayType: derivedArrayType,
                dcToAcRatio: derivedDcToAcRatio,
                inverterEfficiency: derivedInverterEff,
                groundCoverageRatio: derivedGcr,
                albedo: derivedAlbedo,
                bifacial: derivedBifacial,

                // system-level monthly irradiance loss (%)
                monthlyIrradianceLoss,

                // system DC capacity factor (%)
                dcCapacityFactor,

                // loss breakdown (for System Losses table in PDF)
                lossBreakdown: {
                  moduleNameplateRating: values.moduleNameplateRating,
                  mismatch: values.mismatch,
                  connections: values.connections,
                  lightInducedDegradation: values.lightInducedDegradation,
                  wiring: values.wiring,
                  soiling: values.soiling,
                  availability: values.availability,
                  age: values.age,
                  snow: values.snow,
                  other: values.other,
                },

                // raw per-segment PVWatts results + request inputs
                segmentProductions,
                segmentRequestParams,

                // per-segment monthly irradiance loss (%)
                segmentMonthlyIrradianceLoss,
              })
            }
            disabled={segmentProductions.length === 0}
          >
            Download Report
          </Button>
        </Flex>

        <Spin spinning={loading}>
          {monthlyProduction.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                }}
              />
            </div>
          )}

          {/* System Stats Under Chart */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                justifyItems: "between",
              }}
            >
              <Text style={{ fontWeight: 400 }}>
                Production:{" "}
                {loading ? "..." : totalProduction.toFixed(0)} kWh
              </Text>
              <Text>System Capacity: {dcSystemSizeKw.toFixed(2)} kW</Text>
            </div>
            <Text>Total Losses: {totalLoss.toFixed(2)} %</Text>
          </div>
        </Spin>
        <Collapse
          style={{
            marginTop: 16,
            borderRadius: 8,
            overflow: "hidden",
            background: "#fafafa",
            border: "1px solid #e5e5e5",
          }}
          expandIconPosition="end"
        >

          <Panel
            header="System Details"
            key="system"
            style={{
              borderRadius: 8,
              background: "white",
              padding: 0,
            }}
          >
            <div style={{ padding: "12px 16px" }}>
              {/* Module Type */}
              <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 12 }}>Module Type</Text>
                <Select
                  size="small"
                  value={systemDetails.module_type}
                  onChange={(val) => handleSystemDetailChange("module_type", val)}
                  style={{ width: 140 }}
                  options={[
                    { label: "Standard", value: 0 },
                    { label: "Premium", value: 1 },
                    { label: "Thin Film", value: 2 },
                  ]}
                />
              </Flex>

              {/* DC/AC Ratio */}
              <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 12 }}>DC to AC Ratio</Text>
                <InputNumber
                  size="small"
                  value={systemDetails.dc_ac_ratio}
                  onChange={(val) => handleSystemDetailChange("dc_ac_ratio", val)}
                  min={0.5}
                  max={3}
                  step={0.05}
                  style={{ width: 80 }}
                />
              </Flex>

              {/* Inverter Efficiency */}
              <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 12 }}>Inverter Efficiency (%)</Text>
                <InputNumber
                  size="small"
                  value={systemDetails.inv_eff}
                  onChange={(val) => handleSystemDetailChange("inv_eff", val)}
                  min={90}
                  max={99.5}
                  step={0.1}
                  style={{ width: 80 }}
                />
              </Flex>

              {/* Dataset */}
              <Flex justify="space-between" align="center">
                <Text style={{ fontSize: 12 }}>Dataset</Text>
                <Select
                  size="small"
                  value={systemDetails.dataset}
                  onChange={(val) => handleSystemDetailChange("dataset", val)}
                  style={{ width: 140 }}
                  options={[
                    { label: "NSRDB", value: "nsrdb" },
                    { label: "TMY2", value: "tmy2" },
                    { label: "TMY3", value: "tmy3" },
                    { label: "International", value: "intl" },
                  ]}
                />
              </Flex>
            </div>
          </Panel>
        </Collapse>

        {/* Collapsible System Losses */}
        <Collapse
          style={{
            marginTop: 16,
            borderRadius: 8,
            overflow: "hidden",
            background: "#fafafa",
            border: "1px solid #e5e5e5",
          }}
          expandIconPosition="end"
        >
          <Panel
            header="System Losses"
            key="losses"
            style={{
              borderRadius: 8,
              background: "white",
              padding: 0,
            }}
          >
            <div style={{ padding: "12px 16px" }}>
              {Object.entries({
                moduleNameplateRating: "Module nameplate rating",
                mismatch: "Mismatch",
                connections: "Connections",
                lightInducedDegradation: "Light-induced degradation",
                wiring: "Wiring",
                soiling: "Soiling",
                availability: "Availability",
                age: "Age",
                snow: "Snow",
                other: "Other",
              }).map(([key, label]) => (
                <EditableField
                  key={key}
                  label={label}
                  field={key}
                  value={values[key]}
                />
              ))}

              <Text
                style={{
                  marginTop: 8,
                  display: "block",
                  fontWeight: 500,
                }}
              >
                Total Loss: {totalLoss.toFixed(2)}%
              </Text>
            </div>
          </Panel>
        </Collapse>
      </Flex>
    </Card>
  );
};

export default ElectricityGeneration;
